/**
 * Floating toggle button shown when the sources drawer is hidden.
 * Extracted from ResizableSourcePanel.tsx (single-chat-view increment)
 * because the Experience layout in ChatContainer uses it while the rest
 * of the classic resizable panel was deleted.
 */
import { cn } from '@/utils'
import { Badge } from '@/components/ui'
import { FileText, ChevronLeft, ChevronRight, ChevronUp } from 'lucide-react'
import type { SourcePanelPosition } from '@/types/source'

interface SourcePanelToggleProps {
  sourceCount: number
  onClick: () => void
  position: SourcePanelPosition
  /** Optional text label shown next to the icon (e.g. "Sources") */
  label?: string
}

export function SourcePanelToggle({
  sourceCount,
  onClick,
  position,
  label,
}: SourcePanelToggleProps) {
  if (sourceCount === 0) return null

  const getPositionClasses = () => {
    switch (position) {
      case 'right':
        return 'right-4 top-1/2 -translate-y-1/2'
      case 'left':
        return 'left-4 top-1/2 -translate-y-1/2'
      case 'bottom':
        return 'bottom-4 left-1/2 -translate-x-1/2'
    }
  }

  const getIcon = () => {
    switch (position) {
      case 'right':
        return <ChevronLeft className="w-4 h-4" />
      case 'left':
        return <ChevronRight className="w-4 h-4" />
      case 'bottom':
        return <ChevronUp className="w-4 h-4" />
    }
  }

  return (
    <button
      onClick={onClick}
      className={cn(
        'absolute p-2 bg-card',
        'border border-border',
        'rounded-lg shadow-md',
        'hover:bg-accent',
        'transition-colors z-10',
        getPositionClasses()
      )}
    >
      <div className="flex items-center gap-1.5">
        <FileText className="w-4 h-4" />
        {label && <span className="text-xs font-medium">{label}</span>}
        <Badge variant="secondary" className="text-xs">
          {sourceCount}
        </Badge>
        {getIcon()}
      </div>
    </button>
  )
}
